import {serverDomain} from "../constants";
import Query from "../models/Query";
import KeycloakService from "../KeycloakService";
import {SortModel} from "../components/Tables/types";
import {compareValues} from "../components/Tables/compareValues";

/**
 * The part of a column definition this sort needs. Both `Column` (the shared
 * configs in constants.ts) and `LMColumn` (what LMTable consumes) satisfy it.
 */
export interface SortableColumn {
  field: string;
  sortComparator?: (a: any, b: any) => number;
  valueGetter?: (value: any, row: any) => any;
}

/**
 * Sorts an array of objects based on the first criteria in the sort model.
 *
 * Used by the pages that page their rows themselves (they hand LMTable a single
 * slice, so the table can only sort within that slice) — those must sort the full
 * list here, before slicing, or the sort would only reorder the current page.
 *
 * Because LMTable then re-sorts that slice with the column's own comparator, this
 * has to order rows exactly as the column does, or the rows we paged by and the
 * rows the table shows would come out in different orders. So: pass the same
 * column definitions the table is built from, and the column's `sortComparator`
 * (or, absent one, the table's default comparator) is what decides here too.
 *
 * @param data - The array of objects to sort.
 * @param sortModel - The sorting configuration (field and direction).
 * @param columns - The table's column definitions, for the sorted column's
 *   comparator and value getter. Omitting them sorts by the raw field value with
 *   the table's default comparator.
 * @returns A new sorted array.
 */
export function sortBySortModel<T extends Record<string, any>>(
  data: T[],
  sortModel: SortModel,
  columns: readonly SortableColumn[] = []
) {
  const sort = sortModel[0];
  if (!sort || !sort.sort) return data;
  const direction = sort.sort === "asc" ? 1 : -1;

  const column = columns.find(c => c.field === sort.field);
  const comparator = column?.sortComparator ?? compareValues;
  const valueOf = (row: T) =>
    column?.valueGetter ? column.valueGetter(undefined, row) : row[sort.field];

  return [...data].sort((a, b) => direction * comparator(valueOf(a), valueOf(b)));
}

export async function fetchBackend(url: string, method: string = 'GET', bodyObj: any = {}) {
  const token = KeycloakService.instance.getToken()
  const headers: any = {
    "Authorization": `Bearer ${token}`,
  }

  if (method !== 'GET') {
    headers["Content-Type"] = "application/json"
  }

  const response = await fetch(url, {
    method: method,
    headers: headers,
    body: method !== 'GET' ? JSON.stringify(bodyObj) : undefined
  })

  return await response.json()
}

/** A non-2xx response from the backend, carrying its status and error code for the caller. */
export class BackendError extends Error {
  constructor(readonly status: number, readonly code?: string, message?: string) {
    super(message ?? `Request failed with status ${status}`)
    this.name = 'BackendError'
  }
}

/**
 * Like fetchBackend, but REJECTS on a non-2xx response instead of resolving with the parsed error
 * body. fetchBackend only calls response.json(), so a 4xx/5xx resolves normally and is
 * indistinguishable from success to its callers -- which is how a rejected save could report
 * "saved successfully". Use this for any write whose failure the user must be told about.
 */
export async function fetchBackendChecked(url: string, method: string = 'GET', bodyObj: any = {}) {
  const token = KeycloakService.instance.getToken()
  const headers: any = {
    "Authorization": `Bearer ${token}`,
  }

  if (method !== 'GET') {
    headers["Content-Type"] = "application/json"
  }

  const response = await fetch(url, {
    method: method,
    headers: headers,
    body: method !== 'GET' ? JSON.stringify(bodyObj) : undefined
  })

  // An error response need not be JSON (a proxy timeout, an HTML error page), so a parse failure
  // must not mask the status we actually care about reporting.
  const payload = await response.json().catch(() => undefined)

  if (!response.ok) {
    throw new BackendError(response.status, payload?.error, payload?.message)
  }

  return payload
}

export function timeStamp() {
  const currentDate = new Date();

  const day = String(currentDate.getDate()).padStart(2, '0');
  const month = String(currentDate.getMonth() + 1).padStart(2, '0');
  const year = currentDate.getFullYear();

  const formattedDate = `${day}-${month}-${year}`;
  return `${formattedDate} / ${currentDate.toLocaleTimeString('de-DE', {hour12: false})}`;
}

/**
 * The file name from a Content-Disposition header, or undefined if it carries none.
 * Prefers the UTF-8 `filename*` (RFC 6266): for a name with characters outside
 * Latin-1 the plain `filename` holds only a stand-in (Express writes "?ód?" for
 * "Łódź"). Umlauts come as Latin-1 in the plain `filename`.
 */
export function filenameFromContentDisposition(disposition: string | null): string | undefined {
  if (!disposition) return undefined;

  const extended = /filename\*\s*=\s*UTF-8''([^;\s]+)/i.exec(disposition);
  if (extended) {
    try {
      return decodeURIComponent(extended[1]);
    } catch {
      // Malformed percent-encoding: fall through to the plain filename
    }
  }

  const plain = /filename\s*=\s*(?:"([^"]*)"|([^;\s]+))/i.exec(disposition);
  return plain?.[1] || plain?.[2] || undefined;
}

export function applySearchFilter(items: any[], searchFilter: string): any[] {
  return items.filter((elem) => {
    return Object.values(elem).some((value: any) => {
      if (value === undefined || value === null ) {
        return false
      }
      return value.toString().toLowerCase().includes(searchFilter.toLowerCase())
    });
  })
}

export function getGETUrl(endpoint: string, query?: Query) {
  if (!query) {
    return `${serverDomain}/${endpoint}`
  }

  let url = `${serverDomain}/${endpoint}?`
  Object.keys(query).forEach((key) => {
    if (typeof query[key] !== 'string') {
      return
    }
    url = url + `${key}=${query[key]}&`
  })
  url = url.slice(0, -1) // Cut off last "&"
  return url
}

export function sumArray(arr: number[]): number {
  return arr.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}
